import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Play, Sparkles, Wand2, Type, Film, Clock, Zap, ArrowRight, Loader2, Trash2, LogIn, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, loginWithGoogle } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtBytes } from "@/lib/format";
import { localFirstEnabled, readVideoMeta, setLocalAsset } from "@/lib/localAssets";

const DEFAULT_FORMATS = ["mp4", "mov", "webm", "m4v", "mp3"];

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [upCfg, setUpCfg] = useState(null);
  const formats = upCfg?.formats || DEFAULT_FORMATS;
  const ACCEPT = formats.map((f) => `.${f}`);
  const fmtList = formats.map((f) => f.toUpperCase()).join(", ");
  const maxMb = upCfg?.max_mb || 500;
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("auth_error") === "google") {
      toast.error("Google sign-in was unable to complete. Please try again.");
      window.history.replaceState(null, "", "/");
    }
  }, []);
  const [projects, setProjects] = useState([]);
  const [usage, setUsage] = useState(null);
  const [language, setLanguage] = useState("auto");
  const [languages, setLanguages] = useState([{ code: "auto", label: "Auto-detect" }, { code: "en", label: "English" }, { code: "te", label: "Telugu (తెలుగు)" }, { code: "hi", label: "Hindi (हिन्दी)" }, { code: "te-en", label: "Telugu + English" }, { code: "hi-en", label: "Hindi + English" }]);

  useEffect(() => { if (!user) return; api.listProjects().then((p) => setProjects(p.filter((x) => !x.is_demo))).catch(() => {}); api.getLanguages().then(setLanguages).catch(() => {}); api.getUsage().then(setUsage).catch(() => {}); api.uploadConfig().then(setUpCfg).catch(() => {}); }, [user]);

  // Projects are UNLIMITED per user (storage is tracked separately). No creation cap.
  const created = projects.length; // live non-demo count (demo is excluded server-side)
  const [projQuery, setProjQuery] = useState("");
  const [projSort, setProjSort] = useState("updated");
  const refreshProjects = () => { api.listProjects().then((p) => setProjects(p.filter((x) => !x.is_demo))).catch(() => {}); api.getUsage().then(setUsage).catch(() => {}); api.uploadConfig().then(setUpCfg).catch(() => {}); };
  const delProject = async (id, e) => { e.stopPropagation(); if (!window.confirm("Delete this project?")) return; try { await api.deleteProject(id); refreshProjects(); toast.success("Project deleted"); } catch { toast.error("Delete failed"); } };
  const [selected, setSelected] = useState(() => new Set());
  const toggleSelect = (id, e) => { e.stopPropagation(); setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const allSelected = projects.length > 0 && selected.size === projects.length;
  const toggleAll = () => setSelected((s) => (s.size === projects.length ? new Set() : new Set(projects.map((p) => p.id))));
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} project${ids.length > 1 ? "s" : ""}? This action cannot be undone.`)) return;
    try { const r = await api.bulkDeleteProjects(ids); setSelected(new Set()); refreshProjects(); toast.success(`Deleted ${r.deleted} project${r.deleted !== 1 ? "s" : ""}`); }
    catch { toast.error("Bulk delete failed"); }
  };

  const doUpload = useCallback(async (file) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPT.includes(ext)) { toast.error(`This file format isn't supported. Please upload ${fmtList}.`); return; }
    if (file.size > maxMb * 1024 * 1024) { toast.error(`Maximum file size: ${maxMb} MB. Please upload a smaller file.`); return; }
    if (upCfg && upCfg.uploads_remaining === 0) { toast.error(`You've used all ${upCfg.upload_limit} project uploads for this month.`, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } }); return; }
    const name = file.name.replace(/\.[^.]+$/, "");
    if (localFirstEnabled) {
      // Local-first ingestion: create a shell, stash the local blob, and jump straight into
      // the editor. Upload + transcription run in the background from the Editor.
      setUploading(true); setProgress(0); setCurrent({ name: file.name, size: file.size });
      try {
        const meta = await readVideoMeta(file);
        const proj = await api.createShell({
          name, language, duration: meta.duration, width: meta.width, height: meta.height,
          file_size_bytes: file.size, file_name: file.name,
          browser: (navigator.userAgent || "").slice(0, 120),
          connection: (navigator.connection && navigator.connection.effectiveType) || null,
        });
        setLocalAsset(proj.id, { file, url: meta.url, meta });
        navigate(`/editor/${proj.id}`);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Couldn't start the project. Please try again.";
        if (e?.response?.status === 403) toast.error(msg, { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
        else toast.error(msg);
        setUploading(false); setCurrent(null);
      }
      return;
    }
    setUploading(true); setProgress(0);
    setCurrent({ name: file.name, size: file.size });
    try {
      const proj = await api.uploadVideo(file, name, setProgress, language);
      toast.success("Video uploaded — transcribing now");
      navigate(`/editor/${proj.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(msg); setUploading(false); setCurrent(null);
    }
  }, [navigate, language, ACCEPT, fmtList, maxMb, upCfg]);

  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) doUpload(f); };
  const onPick = (e) => { const f = e.target.files?.[0]; if (f) doUpload(f); };

  const tryDemo = async () => {
    setDemoLoading(true);
    try { const proj = await api.createDemo(); navigate(`/editor/${proj.id}`); }
    catch { toast.error("Could not load the demo. Try again."); setDemoLoading(false); }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;

  if (!user) return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pointer-events-none absolute inset-0 grain opacity-[0.15]" />
      <main className="relative mx-auto flex max-w-[900px] flex-col items-center px-5 pb-24 pt-24 text-center">
        <img src="/captionmotion-logo.png" alt="Caption Motion" className="mb-6 h-24 w-24 rounded-2xl object-contain" />
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#00F0FF]"><Sparkles size={13} /> Transcribe · Animate · Create</div>
        <h1 className="max-w-3xl font-display text-5xl font-black leading-[0.98] tracking-tighter sm:text-6xl">Turn Speech Into <span className="bg-gradient-to-r from-[#00F0FF] to-[#8B5CF6] bg-clip-text text-transparent">Animated Captions</span></h1>
        <p className="mt-5 max-w-xl text-base text-[#A1A1AA]">Upload a video, edit its transcript, and generate perfectly timed caption animations automatically.</p>
        <button onClick={loginWithGoogle} data-testid="google-login-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-black transition-colors hover:bg-white/90"><LogIn size={17} /> Continue with Google</button>
        <p className="mt-3 text-xs text-[#666]">Sign in to start creating · unlimited projects</p>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pointer-events-none absolute inset-0 grain opacity-[0.15]" />
      <main className="relative mx-auto max-w-[1200px] px-5 pb-24 pt-16 sm:pt-24">
        <div className="animate-fade-up">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#00F0FF]">
            <Sparkles size={13} /> Speech-timed motion graphics
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-black leading-[0.98] tracking-tighter sm:text-6xl lg:text-7xl">
            Turn Speech Into<br />
            <span className="bg-gradient-to-r from-[#00F0FF] via-[#7dd3fc] to-[#8B5CF6] bg-clip-text text-transparent">Animated Text</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A1A1AA] sm:text-lg">
            Upload a video, edit its transcript, and generate perfectly timed text animations automatically. Your words already have timestamps — now turn them into motion graphics.
          </p>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-3 rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-r from-[#00F0FF]/10 to-[#8B5CF6]/10 p-5 sm:flex-row sm:items-center" data-testid="open-demo-banner">
          <div>
            <div className="text-sm font-bold text-white">Just exploring? Open a ready-made demo project</div>
            <div className="mt-0.5 text-xs text-[#A1A1AA]">Sample video · full transcript · word-level timing · caption timeline · multiple styled, animated layers — then try Upgrade to Pro.</div>
          </div>
          <button data-testid="open-demo-project-btn" onClick={tryDemo} disabled={demoLoading}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#00F0FF] px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-[#00F0FF]/90 disabled:opacity-60">
            {demoLoading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Open Demo Project
          </button>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          {/* Upload zone */}
          <div
            data-testid="upload-zone"
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            className={`animate-fade-up group relative flex min-h-[340px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
              drag ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-white/15 bg-white/[0.02] hover:border-white/30 hover:bg-white/[0.03]"
            }`}
          >
            <input ref={inputRef} type="file" accept={ACCEPT.join(",")} className="hidden" onChange={onPick} data-testid="file-input" />
            {!uploading ? (
              <>
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00F0FF] text-black transition-transform group-hover:scale-105">
                  <Upload size={30} strokeWidth={2.4} />
                </div>
                <div className="font-display text-2xl font-bold tracking-tight">Drop your video or audio here</div>
                <div className="mt-2 text-sm text-[#A1A1AA]" data-testid="upload-formats-hint">or click to browse — {fmtList} · up to {maxMb}MB</div>
                {upCfg?.upload_limit != null && (
                  <div className={`mt-2 text-xs font-semibold ${upCfg.uploads_remaining ? "text-[#A1A1AA]" : "text-[#F59E0B]"}`} data-testid="upload-quota">
                    {upCfg.uploads_remaining} of {upCfg.upload_limit} free uploads left this month
                  </div>
                )}
                <div className="mt-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className="text-xs uppercase tracking-wider text-[#666]">Language</span>
                  <select data-testid="language-select" value={language} onChange={(e) => setLanguage(e.target.value)}
                    className="rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-1.5 text-sm outline-none">
                    {languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <button
                    data-testid="upload-btn"
                    onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-black transition-colors hover:bg-white/90"
                  >
                    <Upload size={16} /> Upload Video
                  </button>
                  <button
                    data-testid="try-sample-btn"
                    onClick={(e) => { e.stopPropagation(); tryDemo(); }}
                    disabled={demoLoading}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-bold text-white transition-colors hover:border-white/30 disabled:opacity-60"
                  >
                    {demoLoading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Try Sample Video
                  </button>
                </div>
              </>
            ) : (
              <div className="w-full max-w-md" data-testid="upload-progress">
                <Film size={30} className="mx-auto mb-4 text-[#00F0FF]" />
                <div className="truncate font-semibold">{current?.name}</div>
                <div className="mb-3 text-xs text-[#A1A1AA]">{fmtBytes(current?.size)}</div>
                <div className="h-2 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-[#00F0FF] transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="mt-2 text-sm font-medium text-[#00F0FF]">{progress}% uploaded</div>
              </div>
            )}
          </div>

          {/* Feature rail */}
          <div className="grid content-start gap-3">
            {[
              { icon: Clock, t: "Word-level timing", d: "Real Whisper timestamps for every single word." },
              { icon: Type, t: "Editable transcript", d: "Fix mistakes without breaking timing." },
              { icon: Wand2, t: "AI style prompt", d: "Describe the look — GPT-5.4 builds the config." },
              { icon: Zap, t: "Transparent export", d: "True-alpha WebM + burned-in MP4 for reels." },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 transition-colors hover:bg-white/[0.04]" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#8B5CF6]/15 text-[#a78bfa]">
                  <f.icon size={17} />
                </div>
                <div>
                  <div className="text-sm font-bold">{f.t}</div>
                  <div className="text-xs leading-relaxed text-[#A1A1AA]">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#A1A1AA]">My Projects</div>
              {projects.length > 0 && (
                <button data-testid="select-all-projects" onClick={toggleAll} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1 text-xs font-semibold text-[#A1A1AA] transition-colors hover:text-white">
                  <span className={`flex h-3.5 w-3.5 items-center justify-center rounded border ${allSelected ? "border-[#00F0FF] bg-[#00F0FF]" : "border-white/30"}`}>{allSelected && <Check size={10} className="text-black" />}</span>
                  {allSelected ? "Deselect All" : "Select All"}
                </button>
              )}
              {selected.size > 0 && (
                <>
                  <span data-testid="selected-count" className="text-xs font-semibold text-white">Selected: {selected.size}</span>
                  <button data-testid="bulk-delete-projects" onClick={bulkDelete} className="flex items-center gap-1.5 rounded-lg border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-xs font-bold text-red-300 transition-colors hover:bg-red-500/20"><Trash2 size={13} /> Delete</button>
                </>
              )}
            </div>
            <div data-testid="project-count" className="rounded-full border border-white/15 px-3 py-1 text-xs font-bold text-white">{`Projects: ${created}`}<span className="ml-1.5 font-medium text-[#00F0FF]">· Unlimited</span></div>
          </div>
          {projects.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <input data-testid="project-search" value={projQuery} onChange={(e) => setProjQuery(e.target.value)} placeholder="Search projects by name…"
                className="w-60 rounded-lg border border-white/15 bg-transparent px-3 py-1.5 text-xs text-white placeholder:text-[#666] focus:border-[#00F0FF]/50 focus:outline-none" />
              <select data-testid="project-sort" value={projSort} onChange={(e) => setProjSort(e.target.value)}
                className="rounded-lg border border-white/15 bg-[#0b0b0b] px-2 py-1.5 text-xs text-white focus:outline-none">
                <option value="updated">Recently updated</option>
                <option value="created">Recently created</option>
                <option value="oldest">Oldest</option>
                <option value="az">Name A–Z</option>
                <option value="za">Name Z–A</option>
              </select>
            </div>
          )}
          {projects.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-[#666]">No projects yet — upload a video above to get started.</div>
          ) : (() => {
            const q = projQuery.trim().toLowerCase();
            const filtered = projects.filter((p) => !q || (p.name || "").toLowerCase().includes(q));
            const ts = (v) => { try { return new Date(v || 0).getTime() || 0; } catch { return 0; } };
            const sorted = [...filtered].sort((a, b) => {
              if (projSort === "az") return (a.name || "").localeCompare(b.name || "");
              if (projSort === "za") return (b.name || "").localeCompare(a.name || "");
              if (projSort === "created") return ts(b.created_at) - ts(a.created_at);
              if (projSort === "oldest") return ts(a.created_at) - ts(b.created_at);
              return ts(b.updated_at || b.created_at) - ts(a.updated_at || a.created_at); // updated (default)
            });
            if (sorted.length === 0) return <div data-testid="project-search-empty" className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-[#666]">No projects match “{projQuery}”.</div>;
            return (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sorted.map((p) => (
                <div key={p.id} data-testid={`project-card-${p.id}`} onClick={() => navigate(`/editor/${p.id}`)}
                  className={`group flex cursor-pointer items-center justify-between rounded-xl border bg-white/[0.02] p-4 text-left transition-colors hover:border-white/25 ${selected.has(p.id) ? "border-[#00F0FF]/60" : "border-white/10"}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <button data-testid={`select-project-${p.id}`} onClick={(e) => toggleSelect(p.id, e)} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected.has(p.id) ? "border-[#00F0FF] bg-[#00F0FF]" : "border-white/30 hover:border-white/60"}`}>{selected.has(p.id) && <Check size={11} className="text-black" />}</button>
                    <div className="min-w-0">
                      <div className="truncate font-semibold">{p.name}</div>
                      <div className="text-xs text-[#A1A1AA]">{p.video?.width}×{p.video?.height} · {p.status}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`delete-project-${p.id}`} onClick={(e) => delProject(p.id, e)} className="rounded-lg p-2 text-[#A1A1AA] transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 size={15} /></button>
                    <ArrowRight size={16} className="shrink-0 text-[#A1A1AA] transition-transform group-hover:translate-x-1 group-hover:text-white" />
                  </div>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
      </main>
      <Footer />
    </div>
  );
}
